import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ATOMIKA_NAVY } from "../lib/maelabord/brand";
import { api } from "../lib/api";
import type { Customer } from "../lib/markadur";
import type { MaelabordTabId } from "../components/maelabord/types";
import { HitakerfiTab } from "../components/maelabord/tabs/HitakerfiTab";
import { FravikTab } from "../components/maelabord/tabs/FravikTab";
import { FlotiTab } from "../components/maelabord/tabs/FlotiTab";
import { TaekiTab } from "../components/maelabord/tabs/TaekiTab";
import { StyringTab } from "../components/maelabord/tabs/StyringTab";

export const Route = createFileRoute("/verkfaeri/atomika-maelabord")({
  component: MaelabordShell,
});

const TABS: { id: MaelabordTabId; label: string; icon: string }[] = [
  { id: "flotinn", label: "Flotinn", icon: "🗺️" },
  { id: "hitakerfi", label: "Hitakerfi", icon: "⚡" },
  { id: "styring", label: "Stýring", icon: "⇄" },
  { id: "fravik", label: "Frávik", icon: "🛡️" },
  { id: "taeki", label: "Tæki", icon: "🔌" },
];

/** Reads the initial customer kennitala from the URL (?customer=…) once. */
function initialCustomerId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    new URLSearchParams(window.location.search).get("customer") ?? undefined
  );
}

function MaelabordShell() {
  // Customer is chosen in the Hitakerfi sidebar (or a ?customer=… deep-link);
  // the header dropdown was removed.
  const [customerId, setCustomerId] = useState<string | undefined>(
    initialCustomerId,
  );
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [tab, setTab] = useState<MaelabordTabId>(
    initialCustomerId() ? "hitakerfi" : "flotinn",
  );

  useEffect(() => {
    api
      .getMarkadurCustomers(true)
      .then((r) => setCustomers(r.customers))
      .catch(() => {});
  }, []);

  const selected = customers.find((c) => c.kennitala === customerId) ?? null;

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 pb-16 pt-6 sm:px-6">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink">
          Atomika stjórnborð
        </h1>
      </div>

      {selected && (
        <div className="mb-4 rounded-xl border border-line bg-white px-4 py-2.5 text-[13px] text-muted-2">
          Sýni gögn fyrir{" "}
          <span className="font-bold text-ink">{selected.name}</span> · kt.{" "}
          {selected.kennitala}
          {selected.dashboardConnected ? " · tengt ✓" : " · engin gögn enn"}
        </div>
      )}

      <div className="mb-6 flex flex-wrap gap-1.5 rounded-xl border border-line bg-white p-1 shadow-sm">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors ${
                active
                  ? "text-white"
                  : "text-muted hover:bg-canvas hover:text-ink"
              }`}
              style={active ? { background: ATOMIKA_NAVY } : undefined}
            >
              <span aria-hidden>{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "flotinn" && <FlotiTab />}

      {tab === "hitakerfi" && (
        <HitakerfiTab
          companyId={customerId}
          customers={customers}
          onSelectCustomer={setCustomerId}
        />
      )}

      {tab === "styring" && (
        <StyringTab
          companyId={customerId}
          customers={customers}
          onSelectCustomer={setCustomerId}
        />
      )}

      {tab === "fravik" && <FravikTab companyId={customerId} />}

      {tab === "taeki" && <TaekiTab />}
    </div>
  );
}
